import { loop, Cmd } from 'redux-loop';
import * as ACTIONS from 'ReduxLoop/ouManager/invoicing/invoicingActions';
import { INVOICING_ADMIN, MANAGER, PEPPOL_METHOD, getEducationLevel } from '../../constants';
import { getAccountingData } from './invoicingCommands';
import { CURRENT_OU_EDUCATION_LEVEL_FETCHED } from 'ReduxLoop/ouManager/schoolManagerActions';
import { addSuccessNotification, addErrorNotification } from 'ReduxLoop/notifications/notificationActions';
import { storeResource, removeResource, executeBatch } from 'ReduxLoop/utils';
import { getCurrentUserManagement } from 'ReduxLoop/appConfig';
import {
  createAccountingEmail,
  createCompanyNumber,
  createGln,
  createGlobalPeppolIdentifier,
  createLocalPeppolIdentifier,
  validateGLNchecksum,
} from 'ReduxLoop/utils/accountingDetailsUtils';
import Batch from '@kathondvla/sri-client/batch';

const initialState = {
  hasErrorLoadingData: false,
  initialised: false,
  accountingDetails: {},
  companyNumber: null,
  glns: {},
  invalidGlns: {}, // invalid GLNs according to api, they are not persisted so we need to know they are not saved
  companyNumberErrors: [],
  identifierValues: {},
  identifierErrors: {},
  childOus: {},
  parent: null,
  institutionNumbers: {},
  addresses: {},
  relations: [],
  peppolMethod: PEPPOL_METHOD.NONE,
  isLoadingGovernedSchools: false,
  isLoading: true,
};

const getPeppolMethod = (accountingDetails, companyNumber) => {
  const peppolIdentifiers = accountingDetails.filter((detail) => detail.type === 'GLOBAL_PEPPOL_IDENTIFIER' || detail.type === 'LOCAL_PEPPOL_IDENTIFIER');
  if (peppolIdentifiers.length === 0) {
    return PEPPOL_METHOD.NONE;
  }
  const hasLocalPeppolIdentifier = peppolIdentifiers.some((detail) => detail.type === 'LOCAL_PEPPOL_IDENTIFIER');
  if (hasLocalPeppolIdentifier) {
    return PEPPOL_METHOD.LOCAL;
  };
  const globalPeppolIdentifier = accountingDetails.find((detail) => detail.type === 'GLOBAL_PEPPOL_IDENTIFIER');
  return globalPeppolIdentifier && companyNumber && globalPeppolIdentifier.peppolIdentifier.href === companyNumber.$$meta.permalink ? PEPPOL_METHOD.GLOBAL : PEPPOL_METHOD.LOCAL;
}

const getIdentifierValues = (accountingDetails, externalIdentifiers) => {
  const identifierValues = {};
  accountingDetails.forEach((detail) => {
    if (!detail.peppolIdentifier) {
      return;
    }
    if (detail.type === 'GLOBAL_PEPPOL_IDENTIFIER') {
      identifierValues[detail.organisationalUnit.href] = externalIdentifiers[detail.peppolIdentifier.href]?.value;
    } else if (detail.type === 'LOCAL_PEPPOL_IDENTIFIER') {
      identifierValues[detail.organisationalUnitRelation.href] = externalIdentifiers[detail.peppolIdentifier.href]?.value;
    }
  });
  return identifierValues;
};

const OU_TYPES_THAT_DO_ACCOUNTING = ['GOVERNINGINSTITUTION', 'EXTERN', 'ONDERWIJSKOEPEL', 'BISDOM'];
const isAccountingInformationNeeded = (ou, educationLevel, rootState) => {
  if (!OU_TYPES_THAT_DO_ACCOUNTING.includes(ou.type)) {
    return false;
  }
  if (rootState.vm.user.isSuperAdmin) {
    return true;
  }
  const currentUserMgmt = getCurrentUserManagement(ou, educationLevel, rootState.vm.user);
  if (!currentUserMgmt) {
    return false;
  }
  if (currentUserMgmt.role !== MANAGER && currentUserMgmt.role !== INVOICING_ADMIN) {
    return false;
  }
  return true;
}

export const invoicingReducer = (state = initialState, action, rootState) => {
  switch (action.type) {

    case CURRENT_OU_EDUCATION_LEVEL_FETCHED: {
      const ou = rootState.schoolManager.currentOrganisationalUnit;
      const educationLevel = getEducationLevel(action.payload.mainstructures.map((ms) => ms.code)) || 'NONE';
      if (!isAccountingInformationNeeded(ou, educationLevel, rootState)) {
        return state;
      }
      return loop(
        { ...initialState, isLoading: true },
        Cmd.run(getAccountingData, {
          args: [ou.$$meta.permalink],
          successActionCreator: ACTIONS.accountingDataFetched,
          failActionCreator: ACTIONS.fetchAccountingDataFailed,
        })
      );
    }

    case ACTIONS.ACCOUNTING_DATA_FETCHED: {
      const companyNumber = Object.values(action.payload.externalIdentifiers).find((id) => id.type === 'COMPANY_NUMBER') || null
      return {
        ...state,
        initialised: true,
        isLoading: false,
        accountingDetails: Object.fromEntries(
          action.payload.accountingDetails
            .filter((ad) => ad.type !== 'EMAIL' || ad.organisationalUnit.href === rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink)
            .map((detail) => [detail.$$meta.permalink, detail])
        ),
        companyNumber,
        glns: Object.fromEntries(action.payload.externalIdentifiers.filter((id) => id.type === 'GLN').map((id) => [id.$$meta.permalink, id])),
        childOus: Object.fromEntries(action.payload.ous.map((ou) => [ou.$$meta.permalink, ou])),
        parent: action.payload.parent,
        institutionNumbers: action.payload.institutionNumbers
          ? Object.fromEntries(action.payload.institutionNumbers.map((num) => [num.organisationalUnit.href, num.value]))
          : {},
        addresses: Object.fromEntries(action.payload.addresses.map((addr) => [addr.organisationalUnit.href, addr])),
        relations: action.payload.relations,
        peppolMethod: getPeppolMethod(action.payload.accountingDetails, companyNumber),
        identifierValues: getIdentifierValues(action.payload.accountingDetails, Object.fromEntries(action.payload.externalIdentifiers.map((id) => [id.$$meta.permalink, id]))),
      };
    }

    case ACTIONS.FETCH_ACCOUNTING_DATA_FAILED: {
      console.error('Failed to fetch accounting data:', action.payload);
      return loop(
        { ...state, hasErrorLoadingData: true, isLoading: false },
        Cmd.action(
          addErrorNotification(
            'Er is een onverwachte fout opgetreden bij het ophalen van de facturatiegegevens.'
          )
        )
      );
    }

    case ACTIONS.UPDATE_PEPPOL_METHOD: {
      const ouHref = rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink;
      const domain = ouHref.split('/')[1];
      const newState = {
        ...state,
        peppolMethod: action.payload,
        accountingDetails: { ...state.accountingDetails },
        glns: { ...state.glns },
      };
      const batch = new Batch();
      let message = 'Peppol-instellingen zijn opgeslagen.';
      const previousGlobalPeppolId = Object.values(state.accountingDetails).find((detail) => detail.type === 'GLOBAL_PEPPOL_IDENTIFIER');
      const localPeppolIds = Object.values(state.accountingDetails).filter((detail) => detail.type === 'LOCAL_PEPPOL_IDENTIFIER');
      if (action.payload === PEPPOL_METHOD.GLOBAL && !previousGlobalPeppolId) {
        message = 'Je facturen zullen vanaf nu via Peppol worden verzonden.';
        // case Peppol was not enabled yet, now enabling global peppol
        const newGlobalPeppolId = createGlobalPeppolIdentifier(domain, state.companyNumber.$$meta.permalink, ouHref);
        newState.accountingDetails[newGlobalPeppolId.$$meta.permalink] = newGlobalPeppolId;
        batch.put(newGlobalPeppolId);
      } else if (action.payload === PEPPOL_METHOD.GLOBAL && previousGlobalPeppolId && (!previousGlobalPeppolId.peppolIdentifier || previousGlobalPeppolId.peppolIdentifier.href !== state.companyNumber.$$meta.permalink)) {
        message = 'Vanaf nu zal het ondernemingsnummer als peppol identificatienummer gebruikt worden.';
        const newGlobalPeppolId = {
          ...previousGlobalPeppolId,
          peppolIdentifier: { href: state.companyNumber.$$meta.permalink },
        }
        newState.accountingDetails[newGlobalPeppolId.$$meta.permalink] = newGlobalPeppolId;
        batch.put(newGlobalPeppolId);
        if (previousGlobalPeppolId.peppolIdentifier) {
          delete newState.glns[previousGlobalPeppolId.peppolIdentifier.href];
          batch.delete(previousGlobalPeppolId.peppolIdentifier.href);
        }
      }
      if (action.payload === PEPPOL_METHOD.GLOBAL && localPeppolIds.length > 0) {
        // when switching to global peppol, remove all local peppol identifiers and their glns
        localPeppolIds.forEach((id) => {
          delete newState.accountingDetails[id.$$meta.permalink];
          delete newState.glns[id.peppolIdentifier.href];
          batch.delete(id.$$meta.permalink);
          batch.delete(id.peppolIdentifier.href);
        });
      }
      if (action.payload === PEPPOL_METHOD.NONE) {
        message = 'Je facturen zullen in de toekomst niet meer via Peppol worden verzonden.';
        newState.accountingDetails = Object.fromEntries(
          Object.values(newState.accountingDetails)
            .filter((detail) => detail.type !== 'GLOBAL_PEPPOL_IDENTIFIER' && detail.type !== 'LOCAL_PEPPOL_IDENTIFIER')
            .map((detail) => [detail.$$meta.permalink, detail])
        );
        newState.glns = {};
        if (previousGlobalPeppolId) {
          batch.delete(previousGlobalPeppolId.$$meta.permalink);
        }
        localPeppolIds.forEach((id) => {
          batch.delete(id.$$meta.permalink);
          batch.delete(id.peppolIdentifier.href);
        });
      }
      /* if (action.payload === PEPPOL_METHOD.LOCAL) {
        const globalPeppolMethod = Object.values(state.accountingDetails).find((detail) => detail.type === 'GLOBAL_PEPPOL_IDENTIFIER');
        if (globalPeppolMethod && globalPeppolMethod.peppolIdentifier.href === state.companyNumber.$$meta.permalink) {
          // when switching to local peppol, clear the company number from the global peppol identifier
          globalPeppolMethod.peppolIdentifier = undefined;
          newState.accountingDetails[globalPeppolMethod.$$meta.permalink] = globalPeppolMethod;
        }
      } */
      newState.identifierValues = getIdentifierValues(Object.values(newState.accountingDetails), { ...newState.glns, [state.companyNumber?.$$meta.permalink]: state.companyNumber });
      if (batch.array.length === 0) {
        return newState;
      }
      return loop(
        newState,
        Cmd.run(executeBatch, {
          args: [`/${domain}/organisationalunits/batch`, batch],
          successActionCreator: () => addSuccessNotification(message),
          failActionCreator: (error) => addErrorNotification(
            `Er is een onverwachte fout opgetreden bij het wijzigen van de Peppol-instellingen.`
          ),
        })
      );
    }

    case ACTIONS.UPDATE_ACCOUNTING_EMAIL: {
      const { email, project } = action.payload;
      const description = project ? `het e-mailadres voor ${project.toLowerCase()}` : 'het algemene e-mailadres voor de boekhouding';
      const getUpdateCmd = (resource) => Cmd.run(storeResource, {
        args: [resource],
        successActionCreator: () => addSuccessNotification(`${description.charAt(0).toUpperCase() + description.slice(1)} is opgeslagen.`),
        failActionCreator: (error) => addErrorNotification(
          `Er is een onverwachte fout opgetreden bij het opslaan van ${description}.`
        ),
      });
      const existingEmail = Object.values(state.accountingDetails).find((detail) => detail.type === 'EMAIL' && ((!detail.project && !project) || detail.project === project));
      if (existingEmail && email) {
        const newDetail = {
          ...existingEmail,
          value: {
            email,
          },
        };
        return loop(
          {
            ...state,
            accountingDetails: {
              ...state.accountingDetails,
              [existingEmail.$$meta.permalink]: newDetail,
            },
          },
          getUpdateCmd(newDetail)
        );
      } else if (existingEmail && !email) {
        const newAccountingDetails = { ...state.accountingDetails };
        delete newAccountingDetails[existingEmail.$$meta.permalink];
        return loop(
          {
            ...state,
            accountingDetails: newAccountingDetails,
          },
          Cmd.run(removeResource, {
            args: [existingEmail.$$meta.permalink],
            successActionCreator: () => addSuccessNotification(`${description.charAt(0).toUpperCase() + description.slice(1)} is verwijderd.`),
            failActionCreator: (error) => addErrorNotification(
              `Er is een onverwachte fout opgetreden bij het verwijderen van ${description}.`
            ),
          })
        );
      } else if (!existingEmail && email) {
        const ouHref = rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink;
        const domain = ouHref.split('/')[1];
        const newDetail = createAccountingEmail(domain, email, ouHref, project);
        return loop(
          {
            ...state,
            accountingDetails: {
              ...state.accountingDetails,
              [newDetail.$$meta.permalink]: newDetail,
            },
          },
          getUpdateCmd(newDetail)
        );
      }
      return state; // No change if no existing email and payload is empty
    }

    case ACTIONS.UPDATE_COMPANY_NUMBER: {
      const companyNumber = action.payload;
      const ouHref = rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink;
      const domain = ouHref.split('/')[1];
      const newCompanyNumber = createCompanyNumber(domain, companyNumber, ouHref);
      
      return loop(
        {
          ...state,
          companyNumberErrors: [],
          companyNumber: newCompanyNumber,
        },
        Cmd.run(storeResource, {
          args: [newCompanyNumber],
          successActionCreator: () => addSuccessNotification('Ondernemingsnummer is opgeslagen.'),
          failActionCreator: (error) => ACTIONS.persistCompanyNumberFailed(error),
        })
      );
    }

    case ACTIONS.UPDATE_GLN: {
      const { relationHref, gln } = action.payload;
      const newState = {
        ...state,
        identifierValues: { ...state.identifierValues, [relationHref]: gln },
        identifierErrors: { ...state.identifierErrors, [relationHref]: [] },
      };
      if (!gln) {
        return newState;
      } else if (!validateGLNchecksum(gln)) {
        newState.identifierErrors[relationHref] = [{ code: 'gln.invalid.checksum', message: 'Het GLN-nummer is ongeldig (foutieve controledigit)' }];
        return newState;
      }

      const batch = new Batch();
      const domain = relationHref.split('/')[1];
      let extIdGln = Object.values(state.glns).find((id) => id.value === gln);
      const existingLocalPeppolIdentifier = Object.values(state.accountingDetails).find((detail) => detail.type === 'LOCAL_PEPPOL_IDENTIFIER' && detail.organisationalUnitRelation.href === relationHref);
      if (!extIdGln) {
        extIdGln = createGln(domain, gln, rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink);
        newState.glns = {
          ...state.glns,
          [extIdGln.$$meta.permalink]: extIdGln,
        };
        batch.put(extIdGln);
      }
      if (!existingLocalPeppolIdentifier) {
        const newLocalPeppolIdentifier = createLocalPeppolIdentifier(domain, extIdGln.$$meta.permalink, relationHref);
        newState.accountingDetails = {
          ...state.accountingDetails,
          [newLocalPeppolIdentifier.$$meta.permalink]: newLocalPeppolIdentifier,
        };
        batch.put(newLocalPeppolIdentifier);
      } else {
        const oldExtIdGln = Object.values(state.glns).find((id) => id.$$meta.permalink === existingLocalPeppolIdentifier.peppolIdentifier.href);
        if (extIdGln.$$meta.permalink !== oldExtIdGln.$$meta.permalink) {
          const updatedLocalPeppolIdentifier = {
            ...existingLocalPeppolIdentifier,
            peppolIdentifier: {
              href: extIdGln.$$meta.permalink,
            },
          };
          newState.accountingDetails = {
            ...state.accountingDetails,
            [updatedLocalPeppolIdentifier.$$meta.permalink]: updatedLocalPeppolIdentifier,
          };
          batch.put(updatedLocalPeppolIdentifier);
          // Check if old gln is still used by other local peppol identifiers
          const isOldGlnStillUsed = Object.values(state.accountingDetails).some(
            (detail) => (detail.type === 'LOCAL_PEPPOL_IDENTIFIER' && detail.peppolIdentifier.href === oldExtIdGln.$$meta.permalink && detail.organisationalUnitRelation.href !== relationHref)
              || (detail.type === 'GLOBAL_PEPPOL_IDENTIFIER' && detail.peppolIdentifier.href === oldExtIdGln.$$meta.permalink)
          );
          if (!isOldGlnStillUsed) {
            const newGlns = { ...newState.glns };
            delete newGlns[oldExtIdGln.$$meta.permalink];
            newState.glns = newGlns;
            if (!state.invalidGlns[oldExtIdGln.$$meta.permalink]) {
              batch.delete(oldExtIdGln);
            }
          }
        }
      }

      if (batch.array.length === 0) {
        return newState;
      }

      return loop(
        newState,
        Cmd.run(executeBatch, {
          args: [`/${domain}/organisationalunits/batch`, batch],
          successActionCreator: () => addSuccessNotification(`GLN is opgeslagen.`),
          failActionCreator: (error) => ACTIONS.persistGlnFailed(error, relationHref, extIdGln.$$meta.permalink),
        })
      );
    }

    case ACTIONS.UPDATE_GOVERNING_INSTITUTION_PEPPOL_IDENTIFIER: {
      const { ouHref, identifier: identifierValue } = action.payload;
      const ouLabel = rootState.schoolManager.currentOrganisationalUnit.type === 'GOVERNINGINSTITUTION' ? 'het bestuur' : 'de organisatie zelf';
      const newState = {
        ...state,
        identifierValues: { ...state.identifierValues, [ouHref]: identifierValue },
        identifierErrors: { ...state.identifierErrors, [ouHref]: [] },
      };
      if (identifierValue && !identifierValue.match(/^[0-9]{13}$/) && identifierValue !== state.companyNumber?.value) {
        return {
          ...state,
          identifierErrors: {
            ...state.identifierErrors,
            [ouHref]: [{ code: 'gln.invalid.format', message: `Voor ${ouLabel} kan je enkel het ondernemingsnummer van ${ouLabel} of een geldig GLN als peppol identificatienummer gebruiken. ${identifierValue} is geen geldig GLN.` }],
          },
        };
      }
      if (!identifierValue) {
        return newState;
      } else if (identifierValue.match(/^[0-9]{13}$/) && !validateGLNchecksum(identifierValue)) {
        newState.identifierErrors[ouHref] = [{ code: 'gln.invalid.checksum', message: `Het GLN-nummer is ongeldig (foutieve controledigit)` }];
        return newState;
      }
      const batch = new Batch();
      const domain = ouHref.split('/')[1];
      let identifier = identifierValue === state.companyNumber.value
        ? state.companyNumber
        : Object.values(state.glns).find((id) => id.value === identifierValue);
      if (!identifier) {
        identifier = createGln(domain, identifierValue, rootState.schoolManager.currentOrganisationalUnit.$$meta.permalink);
        newState.glns = {
          ...state.glns,
          [identifier.$$meta.permalink]: identifier,
        };
        batch.put(identifier);
      } else if (identifier.value !== identifierValue) {
        const newInvalidGlns = { ...newState.invalidGlns };
        delete newInvalidGlns[identifier.$$meta.permalink];
        newState.invalidGlns = newInvalidGlns;
      }

      const existingGlobalPeppolIdentifier = Object.values(state.accountingDetails).find((detail) => detail.type === 'GLOBAL_PEPPOL_IDENTIFIER' && detail.organisationalUnit.href === ouHref);
      if (!existingGlobalPeppolIdentifier) {
        const newGlobalPeppolId = createGlobalPeppolIdentifier(domain, identifier.$$meta.permalink, ouHref);
        newState.accountingDetails = {
          ...state.accountingDetails,
          [newGlobalPeppolId.$$meta.permalink]: newGlobalPeppolId,
        };
        batch.put(newGlobalPeppolId);
      } else if (existingGlobalPeppolIdentifier.peppolIdentifier?.href === identifier.$$meta.permalink) {
        // the value of the identifier is restored to the existing one
        return newState;
      }

      const updatedGlobalPeppolId = {
        ...existingGlobalPeppolIdentifier,
        peppolIdentifier: { href: identifier.$$meta.permalink },
      };
      newState.accountingDetails = {
        ...state.accountingDetails,
        [updatedGlobalPeppolId.$$meta.permalink]: updatedGlobalPeppolId,
      };
      batch.put(updatedGlobalPeppolId);
      if (identifier.type === 'GLN') {
        if (!validateGLNchecksum(identifier.value)) {
          return newState;
        }
      }
      if (
        existingGlobalPeppolIdentifier.peppolIdentifier &&
        state.companyNumber.$$meta.permalink !== existingGlobalPeppolIdentifier.peppolIdentifier?.href &&
        !Object.values(state.accountingDetails).some((detail) => detail.key !== existingGlobalPeppolIdentifier && detail.type === 'LOCAL_PEPPOL_IDENTIFIER' && detail.peppolIdentifier.href === existingGlobalPeppolIdentifier.peppolIdentifier.href)
      ) {
        // old identifier that is not used locally
        delete newState.glns[existingGlobalPeppolIdentifier.peppolIdentifier.href];
        if (!state.invalidGlns[existingGlobalPeppolIdentifier.peppolIdentifier.href]) {
          batch.delete(existingGlobalPeppolIdentifier.peppolIdentifier.href);
        }
      }
      
      return loop(
        newState,
        Cmd.run(executeBatch, {
          args: [`/${domain}/organisationalunits/batch`, batch],
          successActionCreator: () => addSuccessNotification(`Het peppol identificatienummer voor ${ouLabel} is opgeslagen.`),
          failActionCreator: (error) => ACTIONS.persistGlnFailed(error, ouHref, state),
        })
      );
    }

    case ACTIONS.PERSIST_GLN_FAILED: {
      const { error, href, glnHref } = action.payload;
      const newState = { ...state, invalidGlns: { ...state.invalidGlns, [glnHref]: true } };
      let identifierError = {
        code: 'unexpected.error',
        message: 'Er is een onverwachte fout opgetreden bij het opslaan van het GLN.',
      }
      if (Array.isArray(error.body) && error.status === 409) {
        const isUsedByOtherOu = error.body.some((batchElem) => {
          if (batchElem.href.match(/externalidentifiers/) === null) {
            delete newState.glns[batchElem.href];
          }
          const errorArray = batchElem.status === 409 && batchElem.body?.errors;
          console.log('errorArray', errorArray);
          return errorArray && errorArray.some((e) => e.code === 'duplicate.identifier.value');
        })
        if (isUsedByOtherOu) {
          identifierError = { code: 'gln.duplicate', message: 'Dit GLN is al in gebruik bij een andere organisatie.' };
        }
      }
      newState.identifierErrors[href] = [identifierError];
      if (identifierError.code === 'unexpected.error') {
        console.error('Unexpected error when persisting GLN:', action.payload);
        return loop(
          newState,
          Cmd.action(
            addErrorNotification(
              `Er is een onverwachte fout opgetreden bij het opslaan van het GLN.`
            )
          )
        );
      } else {
        return newState;
      }
      
    }

    case ACTIONS.PERSIST_COMPANY_NUMBER_FAILED: {
      const { error } = action.payload;
      const newState = { ...state };
      if (error.status === 409 && error.body?.errors && error.body.errors.some((e) => e.code === 'duplicate.identifier.value')) {
        newState.companyNumberErrors = [{ code: 'duplicate.companynumber', message: 'Dit ondernemingsnummer is al in gebruik bij een andere organisatie. Neem contact op met Katholiek Onderwijs Vlaanderen om dit uit te klaren.' }];
        return newState;
      }
      console.error('Unexpected error when persisting Company Number:', action.payload);
      return loop(
        newState,
        Cmd.action(
          addErrorNotification(
            `Er is een onverwachte fout opgetreden bij het opslaan van het ondernemingsnummer.`
          )
        )
      );
    }

    default:
      return state;
  }
};
